import { createFirebaseBackend } from './firebase';

import type { BackendClients, BackendCommand, BlobStoreClient, IdentityClient } from './contracts';

export { firebaseFunctionCommands } from './commands';
export type {
  InventoryItemMutationData,
  RoleTemplateMutationData,
  RosterPersonMutationData,
} from './commands';

export const backend = createFirebaseBackend();

const VISUAL_LOCAL_DEMO_SCOPE = Object.freeze({
  orgId: 'visual-demo-org',
  projectId: 'visual-demo-tempest',
});

type VisualLocalCommandResponse = {
  response: unknown;
};

function projectScopeFromRequest(request: unknown): { orgId: string; projectId: string } | null {
  if (!request || typeof request !== 'object') {
    return null;
  }

  const { orgId, projectId } = request as Record<string, unknown>;
  if (typeof orgId !== 'string' || typeof projectId !== 'string') {
    return null;
  }

  return { orgId, projectId };
}

function resolveVisualLocalCommandResponse<TRequest, TResponse>(
  command: BackendCommand<TRequest, TResponse>,
  request: TRequest,
): VisualLocalCommandResponse | null {
  if (import.meta.env.VITE_VISUAL_LOCAL_DEMO !== 'true') {
    return null;
  }

  const scope = projectScopeFromRequest(request);
  const isVisualScope = scope?.orgId === VISUAL_LOCAL_DEMO_SCOPE.orgId
    && scope.projectId === VISUAL_LOCAL_DEMO_SCOPE.projectId;
  if (!isVisualScope || command.name !== 'listDailyAttendanceSignInLinks') {
    return null;
  }

  return { response: { links: [] } };
}

export function getBackend() {
  return backend;
}

export function callBackendCommand<TRequest, TResponse>(
  command: BackendCommand<TRequest, TResponse>,
  request: TRequest,
  clients: BackendClients = backend,
): Promise<TResponse> {
  const visualLocalResponse = resolveVisualLocalCommandResponse(command, request);
  if (visualLocalResponse) {
    return Promise.resolve(visualLocalResponse.response as TResponse);
  }

  return clients.commands.call(command, request);
}

export function getIdentityClient(clients: BackendClients = backend): IdentityClient {
  if (!clients.identity) {
    throw new Error('Identity client is unavailable');
  }

  return clients.identity;
}

export function getCurrentIdentitySession(clients: BackendClients = backend) {
  return getIdentityClient(clients).getCurrentSession();
}

export function getBlobStoreClient(clients: BackendClients = backend): BlobStoreClient {
  if (!clients.blobs) {
    throw new Error('Blob store client is unavailable');
  }

  return clients.blobs;
}

function getIdentityToken(
  options?: { forceRefresh?: boolean },
  clients: BackendClients = backend,
): Promise<string | null> {
  return getIdentityClient(clients).getToken(options);
}

export async function requireIdentityToken(
  message = 'Authentication required',
  clients: BackendClients = backend,
): Promise<string> {
  const token = await getIdentityToken(undefined, clients);
  if (!token) {
    throw new Error(message);
  }

  return token;
}
